/**
 * DashboardMainContent Component
 * 대시보드 메인 콘텐츠 - 협력사별 수행현황
 */
import React, { useEffect, useState } from 'react';
import { getItem } from '@/shared/utils/storage';
import { dashboardApi, DashboardWeeklyResponse, DashboardWeeklySupplierPerformance } from '../../../api/dashboardApi';
import Loading from '@/shared/components/feedback/Loading';
import { Supplier } from '@/features/partner/supplierStatus/types/supplier.types';
import { getDashboardDate } from '@/features/dashboard/utils/dashboardUtils';
import DashboardSummaryCards from './DashboardSummaryCards';
import DashboardFilterSection from './DashboardFilterSection';
import WeeklyPerformanceTable from './WeeklyPerformanceTable';
import RealtimePerformanceTable from './RealtimePerformanceTable';

interface DashboardMainContentProps {
    userType: string;
}

type SortColumn = 'finishCnt' | 'acceptanceRate' | 'rejectCnt';

const DashboardMainContent: React.FC<DashboardMainContentProps> = ({ userType }) => {
    const actualUserType = getItem<string>('USER_TYPE') || userType;
    const userId = getItem<string>('USER_ID') || '';
    const [selectedSupplierId, setSelectedSupplierId] = useState<string>(() =>
        actualUserType === 'SUPPLIER' ? userId : '',
    );
    const [fromDate, setFromDate] = useState<string>('');
    const [toDate, setToDate] = useState<string>('');
    const [weeklyData, setWeeklyData] = useState<DashboardWeeklyResponse | null>(null);
    const [topAreaData, setTopAreaData] = useState<DashboardWeeklyResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [viewMode, setViewMode] = useState<'weekly' | 'realtime'>('realtime');
    const [selectedDate, setSelectedDate] = useState<string>(getDashboardDate);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [displayMode, setDisplayMode] = useState<'count' | 'rate'>('rate');
    const [slotCountFilter, setSlotCountFilter] = useState<string>('');
    const [acceptRateFilter, setAcceptRateFilter] = useState<string>('');
    const [sortColumn, setSortColumn] = useState<SortColumn | null>(null);
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

    const cycleSort = (col: SortColumn) => {
        if (sortColumn === col) {
            if (sortDir === 'asc') setSortDir('desc');
            else { setSortColumn(null); setSortDir('asc'); }
        } else {
            setSortColumn(col);
            setSortDir('asc');
        }
    };

    const sortSuppliers = (list: DashboardWeeklySupplierPerformance[]) => {
        if (!sortColumn) return list;
        const dateKey = selectedDate.replace(/-/g, '');
        const getSlotFinishCnt = (s: DashboardWeeklySupplierPerformance) =>
            (s.dailySlotCounts?.[dateKey] || []).reduce(
                (sum: number, c: string) => sum + (parseInt(c.split('/')[0]) || 0),
                0,
            );
        return [...list].sort((a, b) => {
            let aVal: number, bVal: number;
            if (sortColumn === 'finishCnt') {
                aVal = getSlotFinishCnt(a);
                bVal = getSlotFinishCnt(b);
            } else if (sortColumn === 'acceptanceRate') {
                aVal = a.acceptanceRate ?? 0;
                bVal = b.acceptanceRate ?? 0;
            } else {
                aVal = (a.rejectCnt ?? 0) + (a.assignCancelCnt ?? 0) + (a.deliCancelCnt ?? 0);
                bVal = (b.rejectCnt ?? 0) + (b.assignCancelCnt ?? 0) + (b.deliCancelCnt ?? 0);
            }
            return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
        });
    };

    const initializeDefaultDateRange = () => {
        const today = new Date();
        const format = (d: Date) => d.toISOString().split('T')[0];
        const currentDay = today.getDay();
        const diffToWed = (currentDay + 7 - 3) % 7;
        const wed = new Date(today);
        wed.setDate(today.getDate() - diffToWed);
        const tue = new Date(wed);
        tue.setDate(wed.getDate() + 6);
        setFromDate(format(wed));
        setToDate(format(tue));
    };

    const handleViewModeChange = (mode: 'weekly' | 'realtime') => {
        setViewMode(mode);
        if (actualUserType === 'HEADQUARTERS') setSelectedSupplierId('');
        setSlotCountFilter('');
        setAcceptRateFilter('');
        if (mode === 'realtime') setSelectedDate(getDashboardDate());
    };

    const getSummaryStats = () => {
        let activeSuppliers = 0;
        if (viewMode === 'weekly') {
            activeSuppliers = weeklyData?.activeSupplierCount || 0;
        } else {
            activeSuppliers =
                topAreaData?.supplierPerformances?.filter(
                    (s: DashboardWeeklySupplierPerformance) =>
                        (s.finishCnt || 0) >= 1 && (s.riderCount || 0) >= 1,
                    ).length || 0;
        }

        let totalCompleted = 0;
        if (viewMode === 'weekly') {
            totalCompleted = weeklyData?.totalFinishCnt || 0;
        } else {
            const dateKey = selectedDate?.replace(/-/g, '') || '';
            totalCompleted =
                topAreaData?.supplierPerformances?.reduce(
                    (total: number, supplier: DashboardWeeklySupplierPerformance) => {
                        const slotCounts = supplier.dailySlotCounts?.[dateKey] || [];
                        return (
                            total +
                            slotCounts.reduce(
                                (sum: number, countStr: string) =>
                                    sum + (parseInt(countStr.split('/')[0]) || 0),
                                0,
                            )
                        );
                    },
                    0,
                ) || 0;
        }

        const acceptRate =
            viewMode === 'weekly'
                ? weeklyData?.periodAcceptanceRate || 0
                : topAreaData?.periodAcceptanceRate || 0;
        const slotAchievementRate =
            viewMode === 'weekly'
                ? weeklyData?.periodSlotAchievementRate || 0
                : topAreaData?.periodSlotAchievementRate || 0;

        return { activeSuppliers, totalCompleted, acceptRate, slotAchievementRate };
    };

    const getFilteredSuppliers = () => {
        const dataSource = viewMode === 'weekly' ? weeklyData : topAreaData;
        let filtered = dataSource?.supplierPerformances || [];
        if (slotCountFilter && !isNaN(Number(slotCountFilter))) {
            const minSlots = Number(slotCountFilter);
            const validCount = (slotCounts: string[]) =>
                slotCounts.reduce(
                    (sum, c) => sum + (c.includes('/') ? parseInt(c.split('/')[0]) || 0 : 0),
                    0,
                );
            const getValidFinishCnt = (s: DashboardWeeklySupplierPerformance): number => {
                if (viewMode === 'realtime') {
                    const dateKey = selectedDate.replace(/-/g, '');
                    return validCount(s.dailySlotCounts?.[dateKey] || []);
                }
                return Object.values(s.dailySlotCounts || {}).reduce(
                    (total, dayCounts) => total + validCount(dayCounts),
                    0,
                );
            };
            filtered = filtered.filter((s) => getValidFinishCnt(s) >= minSlots);
        }
        if (acceptRateFilter && !isNaN(Number(acceptRateFilter))) {
            const minRate = Number(acceptRateFilter);
            filtered = filtered.filter(
                (s: DashboardWeeklySupplierPerformance) => (s.acceptanceRate || 0) >= minRate,
            );
        }
        return filtered;
    };

    const getCurrentDataSource = () => (viewMode === 'weekly' ? weeklyData : topAreaData);

    useEffect(() => {
        initializeDefaultDateRange();
    }, []);

    const handleSearch = async () => {
        setLoading(true);
        try {
            const baseSuppId =
                actualUserType === 'HEADQUARTERS' ? selectedSupplierId || undefined : userId;
            const res = await dashboardApi.getWeekly({ fromDate, toDate, suppId: baseSuppId });
            setTopAreaData(res);
            setWeeklyData(res);
        } catch { /* 조용히 무시 */ }
        finally { setLoading(false); }
    };

    useEffect(() => {
        const fetchInitialData = async () => {
            if (!fromDate || !toDate) return;
            setLoading(true);
            try {
                const baseSuppId =
                    actualUserType === 'HEADQUARTERS' ? selectedSupplierId || undefined : userId;
                const topRes = await dashboardApi.getWeekly({
                    fromDate: selectedDate,
                    toDate: selectedDate,
                    suppId: baseSuppId,
                });
                setTopAreaData(topRes);
                const weeklyRes = await dashboardApi.getWeekly({
                    fromDate,
                    toDate,
                    suppId: baseSuppId,
                });
                setWeeklyData(weeklyRes);
            } catch { /* 조회 실패 시 조용히 무시 */ }
            finally { setLoading(false); }
        };
        fetchInitialData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fromDate, toDate, selectedDate, actualUserType === 'HEADQUARTERS' ? selectedSupplierId : '']);

    if (loading) return <Loading />

    const summaryStats = getSummaryStats();
    const filteredSuppliers = getFilteredSuppliers();
    const currentDataSource = getCurrentDataSource();

    const suppliersFromData: Supplier[] = (currentDataSource?.supplierPerformances || []).map(
        (s) => ({ id: s.suppId, name: s.suppName }),
    );

    return (
        <div className="dashboard-main-content dashboard-weekly-layout">
            {/* 1. 페이지 헤더 + 토글 버튼 + 날짜 선택 */}
            <div className="page-header dashboard-header-row">
                <h1 className="page-title">협력사별 수행현황</h1>
                <div className="header-controls">
                    <div className="view-mode-toggle">
                        <button
                            type="button"
                            className={`toggle-btn ${viewMode === 'realtime' ? 'active' : ''}`}
                            onClick={() => handleViewModeChange('realtime')}
                        >
                            실시간/일별
                        </button>
                        <button
                            type="button"
                            className={`toggle-btn ${viewMode === 'weekly' ? 'active' : ''}`}
                            onClick={() => handleViewModeChange('weekly')}
                        >
                            주별
                        </button>
                    </div>
                    {viewMode === 'realtime' && (
                        <div className="date-selector">
                            <i className="fas fa-calendar-alt" />
                            <input
                                type="date"
                                value={selectedDate}
                                onChange={(e) => setSelectedDate(e.target.value)}
                                className="date-input"
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* 2. 요약 통계 카드 */}
            <DashboardSummaryCards stats={summaryStats} />

            {/* 3. 필터 */}
            <DashboardFilterSection
                isOpen={isFilterOpen}
                onToggle={() => setIsFilterOpen(!isFilterOpen)}
                userType={actualUserType}
                suppliers={suppliersFromData}
                selectedSupplierId={selectedSupplierId}
                onSupplierChange={setSelectedSupplierId}
                viewMode={viewMode}
                fromDate={fromDate}
                toDate={toDate}
                onDateChange={(start, end) => { setFromDate(start); setToDate(end); }}
                slotCountFilter={slotCountFilter}
                onSlotCountChange={setSlotCountFilter}
                acceptRateFilter={acceptRateFilter}
                onAcceptRateChange={setAcceptRateFilter}
                onSearch={handleSearch}
                onReset={() => {
                    if (actualUserType === 'HEADQUARTERS') setSelectedSupplierId('');
                    setSlotCountFilter('');
                    setAcceptRateFilter('');
                }}
            />

            {/* 4. 수행 현황 헤더 + 건수/수행률 토글 */}
            <div className="supplier-performance-header">
                <h2 className="sub-title">협력사별 수행 현황</h2>
                {viewMode === 'weekly' && (
                    <div className="display-mode-toggle">
                        <button
                            type="button"
                            className={`toggle-btn ${displayMode === 'count' ? 'active' : ''}`}
                            onClick={() => setDisplayMode('count')}
                        >
                            건수
                        </button>
                        <button
                            type="button"
                            className={`toggle-btn ${displayMode === 'rate' ? 'active' : ''}`}
                            onClick={() => setDisplayMode('rate')}
                        >
                            수행률
                        </button>
                    </div>
                )}
            </div>

            {/* 5. 협력사별 수행 현황 표 */}
            <div className="card supplier-performance-card">
                <div className="card-body" style={{ overflowX: 'auto', padding: 0 }}>
                    {viewMode === 'weekly' ? (
                        <WeeklyPerformanceTable
                            supplierPerformances={filteredSuppliers}
                            weekStartDate={currentDataSource?.weekStartDate || ''}
                            weekEndDate={currentDataSource?.weekEndDate || ''}
                            displayMode={displayMode}
                        />
                    ) : (
                        <RealtimePerformanceTable
                            suppliers={sortSuppliers(filteredSuppliers)}
                            currentDate={selectedDate.replace(/-/g, '')}
                            sortColumn={sortColumn}
                            sortDir={sortDir}
                            onCycleSort={cycleSort}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default DashboardMainContent;
